/**
 * Recovery codes, shown once (two-step sign-in; ported from the console's BackupCodesPage): after the setup forced
 * at sign-in, after a first factor or a regeneration on the Sign-in security page. Copy and Download (.txt), and a
 * required "I have saved these codes" tick before going on — the backend keeps only hashes.
 *
 * `RecoveryCodesLeftStep`: after a sign-in with a recovery code, how many are left and the way to the Sign-in
 * security page (not forced).
 */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { Alert, Box, Button, Checkbox, FormControlLabel, Link, Paper, Stack, Typography } from '@mui/material';
import React from 'react';

import { SIGN_IN_UNREACHABLE } from '../store/contextStore';
import { copyTextToClipboard } from '../utils/clipboard';

export const ACCOUNT_SECURITY_PATH = '/account/security';

const fileText = (codes, account) => [
    'TourOptima recovery codes',
    ...(account ? [account] : []),
    `Created ${new Date().toISOString().slice(0, 10)}. Each code works once; keep them somewhere safe.`,
    '',
    ...codes,
    '',
].join('\n');

const download = (text, fileName) => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

export default function RecoveryCodesStep({ codes, account = null, fileName = 'touroptima-recovery-codes.txt', doneLabel = 'Continue', onDone }) {
    const [saved, setSaved] = React.useState(false);
    const [copied, setCopied] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);

    // Signs in (the sign-in) or closes (the Sign-in security page); a failure keeps the codes on screen.
    const done = async () => {
        setBusy(true);
        setError(null);
        try {
            await onDone();
        } catch (err) {
            setError(SIGN_IN_UNREACHABLE);
            setBusy(false);
        }
    };

    return (
        <Box sx={{ width: '100%', mt: 2 }}>
            <Typography variant="h6" component="h2">Save your recovery codes</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                If you lose your phone, sign in with one of these codes instead. Each code works once. They are shown
                only now.
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Box
                    component="ul"
                    aria-label="Recovery codes"
                    sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, listStyle: 'none', m: 0, p: 0 }}
                >
                    {codes.map((c) => (
                        <Box component="li" key={c} sx={{ fontFamily: 'monospace', fontSize: 16 }}>{c}</Box>
                    ))}
                </Box>
            </Paper>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                    startIcon={<ContentCopyIcon />}
                    onClick={() => {
                        copyTextToClipboard(codes.join('\n'));
                        setCopied(true);
                    }}
                >
                    {copied ? 'Copied' : 'Copy'}
                </Button>
                <Button startIcon={<DownloadIcon />} onClick={() => download(fileText(codes, account), fileName)}>
                    Download
                </Button>
            </Stack>
            <FormControlLabel
                sx={{ mt: 1 }}
                control={<Checkbox checked={saved} onChange={(ev) => setSaved(ev.target.checked)} />}
                label="I have saved these codes"
            />
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            <Button fullWidth variant="contained" color="primary" sx={{ mt: 1 }} disabled={!saved || busy} onClick={done}>
                {doneLabel}
            </Button>
        </Box>
    );
}

export function RecoveryCodesLeftStep({ remaining, onContinue, onOpenSecurity }) {
    const [busy, setBusy] = React.useState(false);
    const go = (fn) => async () => {
        setBusy(true);
        try {
            await fn();
        } catch (err) {
            setBusy(false);
        }
    };
    const count = remaining === 0 ? 'No recovery codes left' : `${remaining} recovery code${remaining === 1 ? '' : 's'} left`;
    return (
        <Box sx={{ width: '100%', mt: 2 }}>
            <Alert severity={remaining <= 2 ? 'warning' : 'info'}>
                {count} — set up a new authenticator or regenerate your codes on the{' '}
                <Link
                    href={ACCOUNT_SECURITY_PATH}
                    onClick={(ev) => {
                        ev.preventDefault();
                        if (!busy) go(onOpenSecurity)();
                    }}
                >
                    Sign-in security
                </Link>{' '}
                page.
            </Alert>
            <Button fullWidth variant="contained" color="primary" sx={{ mt: 2 }} disabled={busy} onClick={go(onContinue)}>
                Continue
            </Button>
        </Box>
    );
}
